import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

// Convert URL-safe base64 to Uint8Array (needed for applicationServerKey)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);
  const [registration, setRegistration] = useState(null);

  // Check support and get current state
  useEffect(() => {
    const checkSupport = async () => {
      // Check if push is supported
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setIsSupported(supported);
      
      if (!supported) {
        setLoading(false);
        return;
      }
      
      // Get current permission
      setPermission(Notification.permission);
      
      try {
        // Fetch VAPID public key from backend
        const response = await api.get('/push/vapid-key');
        if (response.data.push_available) {
          setVapidPublicKey(response.data.vapid_public_key);
        }
        
        // Register service worker
        const reg = await navigator.serviceWorker.register('/sw.js');
        setRegistration(reg);
        
        // Check if already subscribed
        const subscription = await reg.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
        
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Push notification setup error:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    
    checkSupport();
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !vapidPublicKey || !registration) {
      throw new Error('Push notifications not available');
    }
    
    // Request permission if not granted
    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        throw new Error('Notification permission denied');
      }
    } else if (Notification.permission === 'denied') {
      throw new Error('Notification permission blocked. Please enable in browser settings.');
    }
    
    try {
      // Subscribe to push manager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      await api.post('/push/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });
      
      setIsSubscribed(true);
      return true;
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Push subscription error:', error);
      }
      throw error;
    }
  }, [isSupported, vapidPublicKey, registration]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!registration) return;
    
    try {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();
        
        // Remove from backend
        const subscriptionJson = subscription.toJSON();
        await api.delete('/push/unsubscribe', {
          data: {
            endpoint: subscriptionJson.endpoint,
            keys: subscriptionJson.keys
          }
        });
      }
      
      setIsSubscribed(false);
      return true;
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Push unsubscribe error:', error);
      }
      throw error;
    }
  }, [registration]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe
  };
}

export default usePushNotifications;
